/**
 * 构建 R2 存储的图片 URL
 * @param filename 图片文件名（例如：input_ai_figure.webp）
 * @param basePath R2 存储的基础路径，默认为 "ai-image-generator/carousel"
 * @returns 完整的图片 URL
 */
export function buildR2ImageUrl(
	filename: string,
	basePath = "ai-image-generator/carousel",
): string {
	const baseUrl =
		process.env.NEXT_PUBLIC_R2_MEDIA_URL || "https://media.imagesart.ai";
	// 确保 basePath 不以 / 开头，filename 不以 / 开头
	const cleanBasePath = basePath.replace(/^\/+|\/+$/g, "");
	const cleanFilename = filename.replace(/^\/+/, "");
	return `${baseUrl}/${cleanBasePath}/${cleanFilename}`;
}

/**
 * 构建 R2 Assets 存储的图片 URL（使用 imagesart-assets bucket）
 * @param path R2 存储的完整路径（例如：home/showcase/showcase-01.webp 或 ai-image-generator/carousel/input_model_reference.webp）
 * @returns 完整的图片 URL
 */
export function buildR2AssetsImageUrl(path: string): string {
	const baseUrl =
		process.env.NEXT_PUBLIC_R2_ASSETS_URL || "https://assets.imagesart.ai";
	// 确保 path 不以 / 开头
	const cleanPath = path.replace(/^\/+/, "");
	return `${baseUrl}/${cleanPath}`;
}

/**
 * 把一个远端图片地址改成走 Next 图片优化器，拿到按尺寸缩放 + WebP 的版本。
 *
 * 为什么需要它：R2 上的素材是直链引用的，浏览器拿到的就是原图。/image-to-video
 * 的样片海报是 1280×720 的 JPEG，一张 70–155KB，而页面上最大只显示到 768px、
 * 缩略图只有 144px —— 2026-09-15 实测这一页 2,992KB 的图片/媒体里只有 1 个请求
 * 走了优化器（首页是 49 个），Lighthouse 的 image-delivery 可省 745KB。
 *
 * 用之前确认两件事，否则优化器直接回 400：
 *   1. 图片所在 host 必须在 next.config.ts 的 images.remotePatterns 里；
 *   2. width 必须是 deviceSizes/imageSizes 里的值（本项目没自定义，用 Next 默认：
 *      640/750/828/1080/1200/1920/2048/3840 与 16/32/48/64/96/128/256/384），
 *      quality 必须在 images.qualities（本项目是 60/75/80/90）里。
 *
 * 源地址上的查询串（比如素材的 ?v=<内容哈希>）会被编码进 url 参数，照常生效。
 */
export function buildOptimizedImageUrl(
	src: string,
	width: number,
	quality = 75,
): string {
	return `/_next/image?url=${encodeURIComponent(src)}&w=${width}&q=${quality}`;
}

/** 为表单预览生成优化后的展示地址；原始地址仍用于提交生成请求。 */
export function buildDisplayImageUrl(src: string, width: number): string {
	return /^https?:\/\//.test(src) ? buildOptimizedImageUrl(src, width) : src;
}
